import "../../datagrid.scss";
import { useContext, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ZonasContext } from '../../../context/zonasentrega/ZonasContext';
import { Paginator } from '../../../components/paginator/Paginator';
import { headers } from "./configGrid/headers";
import { formatRows } from "./configGrid/rows";
import { indice } from "../../../components/DataGrid/indice";
import { getActionColumn } from "../../../components/ActionesColumns/ActionColumns";
import CircularProgress from "@mui/material/CircularProgress";
import useDocumentTitle from "../../../components/Title";
import CustomDataGrid from "../../../components/DataGrid/CustomDataGrid";
import ConfirmDialog from "../../../components/dialogs/DeleteDialog";
import ErrorDialog from "../../../components/dialogs/ErrorDeleteDialog";
import { fetchEntityData, deleteItem } from './configGrid/apiCalls';
import FiltersGrid from './filtersGrid/filtersGrid';
import { useFilters } from "../../../context/FiltersContext";

export const Datatable = ({ onSelect, onClose, enableCreate = true }) => {
  const titleGrid = "Zonas de Entrega";
  useDocumentTitle(titleGrid);
  const { updateFilters, getFilters } = useFilters();
  const contexto = useContext(ZonasContext);
  const { deleteZonaData, getZonasPanelData } = contexto;
  const navigate = useNavigate();

  const nameData = "zonas";
  const descData = "nombre";
  const getData = getZonasPanelData;
  const deleteData = deleteZonaData;

  // Estado para Datos y Paginación
  const [listaItems, setListaItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalDataItems, setTotalDataItems] = useState(0);
  // Estado para Loading
  const [isLoading, setIsLoading] = useState(false);
  // Estados para la paginación
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [numPagina, setNumPagina] = useState(1);
  const [filas, setFilas] = useState(isMobile ? 20 : 100);
  // Estado del buscador
  const [searchTerm, setSearchTerm] = useState("");
  // Ref para búsqueda
  const searchInputRef = useRef(null);
  const isFetchingRef = useRef(false);
  // Estado para el modal de confirmación
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  // Estado para manejar el error
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  // Estado General de Filtros en LocalStorage
  const [isInitialized, setIsInitialized] = useState(false);
  const pageKey = "zonas";

  // Obtenemos los filtros persistidos
  const filters = getFilters(pageKey) || {
    searchTerm: "",
  };

  useEffect(() => {
    const savedFilters = getFilters(pageKey);

    if (savedFilters) {
      setSearchTerm(savedFilters.searchTerm || "");
      setNumPagina(1);
    }

    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    fetchData();
    loadFilters();
  }, [isInitialized, searchTerm, numPagina, filas]);

  // Función segura para actualizar filtros
  const loadFilters = () => {
    const updatedFilters = {
      searchTerm,
    };
    updateFilters(pageKey, updatedFilters);
  };

  // Fetch de datos
  const fetchData = async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const { dataEntity, totalItems, totalPage, totalDataItems } = await fetchEntityData(
        getData,
        searchTerm,
        numPagina,
        filas
      );
      if (numPagina > totalPage) {
        setNumPagina(totalPage);
      } else {
        setListaItems(dataEntity);
        setTotalItems(totalItems);
        setTotalDataItems(totalDataItems);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  const handleDelete = async () => {
    try {
      await deleteItem(deleteData, selectedItem);
      fetchData();
      closeModal();
    } catch (error) {
      console.error(error);
      setErrorMessage("No se pudo eliminar la zona. Inténtalo nuevamente.");
      setIsErrorDialogOpen(true);
    }
  };

  const closeErrorDialog = () => {
    setErrorMessage("");
    setIsErrorDialogOpen(false);
  };

  const openModal = (id) => {
    setSelectedItem(id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedItem(null);
    setIsModalOpen(false);
  };

  const handleClickEdit = (e) => {
    if (e) navigate(`/${nameData}/new/${e}`);
  };

  const handleSearch = () => {
    const value = searchInputRef.current?.value || "";
    setSearchTerm(value);
  };

  const handleClear = () => {
    setSearchTerm("");
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
  };

  const getDynamicValue = (row, key) => {
    return row[key];
  };

  const handleRowClick = (params) => {
    const selectedId = params.row.id;
    const selectedDesc = getDynamicValue(params.row, descData);
    onSelect && onSelect(selectedId, selectedDesc);
    onClose && onClose();
  };

  const datosColum = useMemo(() => {
    const commonCols = indice(numPagina, filas).concat(headers.filter((column) => !column.hidden));
    return onSelect ? commonCols : commonCols.concat(getActionColumn(handleClickEdit, openModal));
  }, [onSelect, numPagina, filas]);

  const rows = useMemo(() => formatRows(listaItems), [listaItems]);

  return (
    <div className="datatable">
      <div className="datatableTitle">
        <h3>{titleGrid}</h3>
        {enableCreate && (
          <div className="linkContainer">
            <Link to={`/${nameData}/new`} className="link">Agregar Zona</Link>
          </div>
        )}
        <FiltersGrid
          searchInputRef={searchInputRef}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          onClose={handleClear}
        />
      </div>

      {isLoading ? (
        <div className="loading">
          <div style={{ display: "flex", justifyContent: "center", margin: "20px" }}>
            <h1>Cargando datos...</h1>
            <CircularProgress />
          </div>
        </div>
      ) : (
        <> 
            <CustomDataGrid
              rows={rows}
              columns={datosColum}
              pageSize={filas}
              rowCount={totalItems}
              onPageChange={setNumPagina}
              onPageSizeChange={(newPageSize) => {
                setFilas(newPageSize);
                setNumPagina(1);
              }}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              enableSelectionClick={false}
            /> 
          <div className="paginatorWrapper">
            <Paginator
              numPagina={numPagina}
              setNumPagina={setNumPagina}
              totalPages={Math.ceil(totalItems / filas)}
              filas={filas}
              setFilas={setFilas}
              totalDataItems={totalDataItems}
            />
          </div>
          <ConfirmDialog
            open={isModalOpen}
            onClose={closeModal}
            onConfirm={handleDelete}
            nameData={nameData}
          />
          <ErrorDialog
            open={isErrorDialogOpen}
            onClose={closeErrorDialog}
            errormessages={errorMessage}
          />
        </>
      )}
    </div>
  );
};

export default Datatable;
